import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, FloppyDisk, ClockClockwise, Sparkle } from "@phosphor-icons/react";
import api from "../../lib/api";

const FREQ_PRESETS = [
  { hours: 24, label: "Daily" },
  { hours: 72, label: "Every 3 days" },
  { hours: 168, label: "Weekly (recommended)" },
  { hours: 336, label: "Bi-weekly" },
  { hours: 720, label: "Monthly" },
  { hours: 0, label: "Disabled" },
];

export default function SEOPanel() {
  const [cfg, setCfg] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [freqHours, setFreqHours] = useState(168);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, k] = await Promise.all([
        api.get("/admin/seo/config").then((r) => r.data),
        api.get("/admin/seo/keywords").then((r) => r.data),
      ]);
      setCfg(c);
      setKeywords(k);
      setFreqHours(c?.cron_freq_hours ?? 168);
      setEnabled(c?.enabled ?? true);
    } catch (e) {
      toast.error("Failed to load SEO config: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/seo/config", {
        cron_freq_hours: Number(freqHours),
        enabled,
      });
      toast.success("SEO cron config saved");
      await reload();
    } catch (e) {
      toast.error("Failed to save SEO config: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const refreshNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/seo/refresh-now");
      if (data.ok) {
        toast.success(`Pulled ${data.total_keywords} fresh keywords`);
      } else {
        toast.warning(`Partial: ${data.status || data.reason || "no keywords returned"}`);
      }
      await reload();
    } catch (e) {
      toast.error("Manual refresh failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="text-muted-foreground" data-testid="seo-panel-loading">Loading SEO config…</div>;
  }

  const lastRun = cfg?.last_run_at ? new Date(cfg.last_run_at).toLocaleString() : "never";
  const counts = cfg?.dynamic_keywords_per_slug || {};
  const totalDynamic = Object.values(counts).reduce((a, b) => a + b, 0);
  const dyn = keywords?.dynamic_keywords || {};
  const history = keywords?.history || [];

  return (
    <div className="space-y-8" data-testid="seo-admin-panel">
      <header>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl mb-1">SEO cron & keyword pool</h2>
            <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
              Claude Sonnet 4.5 (via Emergent LLM key) refreshes the dynamic
              keyword pool that gets merged into the 10 long-tail SEO landing
              pages (<code>/seo/&lt;slug&gt;</code>). Adjust frequency below or
              run a refresh now.
            </p>
          </div>
          <button
            type="button"
            onClick={refreshNow}
            disabled={running}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm"
            data-testid="seo-refresh-now"
          >
            <Sparkle size={16} weight={running ? "regular" : "fill"} />
            {running ? "Refreshing…" : "Refresh keywords now"}
          </button>
        </div>
      </header>

      <section className="grid md:grid-cols-3 gap-4" data-testid="seo-stats">
        <StatCard label="Total dynamic keywords" value={totalDynamic} testid="seo-total-kw" />
        <StatCard label="Pages with dynamic keywords" value={Object.keys(counts).length + "/10"} testid="seo-pages-with-kw" />
        <StatCard
          label="Last refresh"
          value={lastRun}
          sub={cfg?.last_run_status || ""}
          testid="seo-last-refresh"
        />
      </section>

      <section className="card-flat p-6 space-y-4" data-testid="seo-cron-form">
        <h3 className="font-serif text-lg">Cron schedule</h3>
        <div className="grid md:grid-cols-2 gap-4 items-end">
          <label className="block">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              Frequency
            </span>
            <select
              value={freqHours}
              onChange={(e) => setFreqHours(Number(e.target.value))}
              className="mt-2 w-full bg-background border hairline rounded-md px-3 py-2 text-sm"
              data-testid="seo-freq-select"
            >
              {FREQ_PRESETS.map((p) => (
                <option key={p.hours} value={p.hours}>
                  {p.label} ({p.hours === 0 ? "0" : `${p.hours}h`})
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="seo-enabled-toggle"
            />
            <span>Cron enabled</span>
          </label>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm"
            data-testid="seo-save-config"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving…" : "Save schedule"}
          </button>
          <button
            type="button"
            onClick={reload}
            className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-sm border hairline rounded-md"
            data-testid="seo-reload"
          >
            <ArrowsClockwise size={14} weight="bold" /> Reload
          </button>
        </div>
      </section>

      <section className="card-flat p-6 space-y-4" data-testid="seo-keywords-pool">
        <h3 className="font-serif text-lg">Dynamic keyword pool</h3>
        {Object.keys(dyn).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No dynamic keywords yet. Click <em>Refresh keywords now</em> above
            to seed the pool.
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(dyn).map(([slug, list]) => (
              <div key={slug} className="border-b hairline pb-3 last:border-0">
                <div className="font-mono text-xs text-muted-foreground mb-2">
                  /seo/{slug}
                </div>
                <div className="flex flex-wrap gap-2">
                  {list.map((kw, idx) => (
                    <span
                      key={`${slug}-${idx}`}
                      className="inline-block text-xs px-2 py-1 rounded-full bg-[hsl(var(--muted))]"
                      data-testid={`seo-kw-${slug}-${idx}`}
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card-flat p-6 space-y-3" data-testid="seo-history">
        <h3 className="font-serif text-lg inline-flex items-center gap-2">
          <ClockClockwise size={18} weight="duotone" /> Refresh history
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No history yet — cron has not run.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="text-left">
                <th className="py-2 pr-3">Run at</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">By</th>
                <th className="py-2 pr-3">Keywords</th>
                <th className="py-2 pr-3">Duration</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, idx) => (
                <tr key={h.run_at + ":" + h.triggered_by} className="border-t hairline" data-testid={`seo-history-row-${idx}`}>
                  <td className="py-2 pr-3 font-mono">
                    {new Date(h.run_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={
                        h.status === "ok"
                          ? "text-[hsl(var(--primary))]"
                          : "text-muted-foreground"
                      }
                    >
                      {h.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{h.triggered_by}</td>
                  <td className="py-2 pr-3">{h.total_keywords}</td>
                  <td className="py-2 pr-3">{Math.round(h.duration_sec)}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <BroadcastSection />
    </div>
  );
}

function BroadcastSection() {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [minNew, setMinNew] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/admin/seo/broadcast/config").then((r) => r.data),
        api.get("/admin/seo/broadcast/history").then((r) => r.data),
      ]);
      setCfg(c);
      setHistory(h?.history || []);
      setEnabled(!!c.enabled);
      setWebhookUrl(c.webhook_url || "");
      setMinNew(c.min_new_keywords || 3);
    } catch (e) {
      toast.error("Failed to load broadcast config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = async () => {
    if (enabled && !webhookUrl.trim()) {
      toast.error("Webhook URL is required when broadcast is enabled");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/seo/broadcast/config", {
        enabled,
        webhook_url: webhookUrl.trim(),
        hmac_secret: secret,
        min_new_keywords: Number(minNew),
        channels: ["linkedin"],
      });
      toast.success("Broadcast config saved");
      setSecret("");
      await reload();
    } catch (e) {
      toast.error(
        "Save failed: " + (e.response?.data?.detail || e.message || "unknown")
      );
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/seo/broadcast/test");
      if (data.ok) {
        toast.success(`Test sent · n8n returned ${data.status_code} for ${data.items} item(s)`);
      } else {
        toast.warning(
          `Test failed: ${data.error || data.reason || `HTTP ${data.status_code || "?"}`}`
        );
      }
      await reload();
    } catch (e) {
      toast.error("Test failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  const refreshAndBroadcast = async () => {
    setBroadcasting(true);
    try {
      const { data } = await api.post("/admin/seo/refresh-and-broadcast");
      if (data.queued) {
        toast.success("Queued! Refresh + broadcast running in background. Check history in 1-3 minutes.");
      } else if (data.broadcast?.ok) {
        toast.success(`Broadcast sent · ${data.broadcast.items} item(s) to n8n`);
      } else if (data.broadcast) {
        toast.warning(
          `Refresh ok; broadcast: ${data.broadcast.error || data.broadcast.reason || `HTTP ${data.broadcast.status_code || "?"}`}`
        );
      } else {
        toast.info("Refresh ok; no new keywords above threshold for broadcast.");
      }
      await reload();
    } catch (e) {
      toast.error("Refresh & broadcast failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBroadcasting(false);
    }
  };

  const clearSecret = async () => {
    if (!window.confirm("Clear the saved HMAC secret? n8n will reject signed requests until you set a new one.")) return;
    try {
      await api.put("/admin/seo/broadcast/config", {
        enabled,
        webhook_url: webhookUrl.trim(),
        hmac_secret: "",
        clear_hmac_secret: true,
        min_new_keywords: Number(minNew),
        channels: ["linkedin"],
      });
      toast.success("HMAC secret cleared");
      await reload();
    } catch (e) {
      toast.error("Clear failed: " + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return (
      <section className="card-flat p-6" data-testid="seo-broadcast-section-loading">
        <div className="text-muted-foreground">Loading broadcast config…</div>
      </section>
    );
  }

  const lastBroadcast = cfg?.last_run_at ? new Date(cfg.last_run_at).toLocaleString() : "never";

  return (
    <section className="card-flat p-6 space-y-5" data-testid="seo-broadcast-section">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h3 className="font-serif text-lg mb-1">n8n broadcast (SEO → LinkedIn flywheel)</h3>
          <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
            When the SEO cron picks up new trending keywords, fire an
            HMAC-signed webhook to your n8n instance. n8n owns the actual
            publishing to LinkedIn (recipe at <code>/app/docs/n8n-seo-flywheel.json</code>).
            Estima drafts the LinkedIn post + 3 hook variants per slug using
            Claude Sonnet 4.5.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="seo-broadcast-enabled-toggle"
            />
            <span>Broadcast enabled</span>
          </label>
          <div className="text-xs text-muted-foreground">
            Last broadcast: {lastBroadcast}
            {cfg?.last_run_status ? ` · ${cfg.last_run_status}` : ""}
          </div>
        </div>
      </header>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            n8n webhook URL
          </span>
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://n8n.estima.co.in/webhook/estima-seo-flywheel"
            className="mt-2 w-full bg-background border hairline rounded-md px-3 py-2 text-sm font-mono"
            data-testid="seo-broadcast-webhook-url"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            HMAC shared secret {cfg?.hmac_secret_set && `(current: ${cfg.hmac_secret_preview})`}
          </span>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={cfg?.hmac_secret_set ? "Leave blank to keep current" : "e.g. 32-char random string"}
            className="mt-2 w-full bg-background border hairline rounded-md px-3 py-2 text-sm font-mono"
            data-testid="seo-broadcast-secret"
          />
          {cfg?.hmac_secret_set && (
            <button
              type="button"
              onClick={clearSecret}
              className="text-[11px] text-amber-500 hover:underline mt-1"
              data-testid="seo-broadcast-clear-secret"
            >
              Clear saved secret
            </button>
          )}
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            Min new keywords to auto-broadcast
          </span>
          <input
            type="number"
            min={1}
            max={20}
            value={minNew}
            onChange={(e) => setMinNew(e.target.value)}
            className="mt-2 w-full bg-background border hairline rounded-md px-3 py-2 text-sm"
            data-testid="seo-broadcast-threshold"
          />
          <span className="text-[11px] text-muted-foreground">
            Only slugs where ≥ N keywords are NEW vs last run trigger an auto-broadcast.
          </span>
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            Channels (v1)
          </span>
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 text-sm border hairline rounded-md bg-background/50">
            <span className="font-mono">linkedin</span>
            <span className="text-xs text-muted-foreground">· X, Telegram in future</span>
          </div>
        </label>
      </div>

      <div className="flex gap-3 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm"
          data-testid="seo-broadcast-save"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving…" : "Save broadcast config"}
        </button>
        <button
          type="button"
          onClick={sendTest}
          disabled={testing || !cfg?.webhook_url}
          className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-sm border hairline rounded-md"
          data-testid="seo-broadcast-test"
        >
          <Sparkle size={14} />
          {testing ? "Sending…" : "Send test broadcast"}
        </button>
        <button
          type="button"
          onClick={refreshAndBroadcast}
          disabled={broadcasting || !cfg?.webhook_url}
          className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-sm border hairline rounded-md"
          data-testid="seo-broadcast-refresh-and-go"
        >
          <ArrowsClockwise size={14} weight="bold" />
          {broadcasting ? "Working…" : "Refresh & broadcast now"}
        </button>
      </div>

      <div data-testid="seo-broadcast-history">
        <h4 className="font-serif text-sm mb-2">Broadcast history</h4>
        {history.length === 0 ? (
          <p className="text-xs text-muted-foreground">No broadcasts yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="text-left">
                <th className="py-2 pr-3">Sent at</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Items</th>
                <th className="py-2 pr-3">By</th>
                <th className="py-2 pr-3">Slugs</th>
                <th className="py-2 pr-3">HTTP</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 10).map((h, idx) => (
                <tr
                  key={h.run_at + ":" + (h.triggered_by || "")}
                  className="border-t hairline"
                  data-testid={`seo-broadcast-history-row-${idx}`}
                >
                  <td className="py-2 pr-3 font-mono">
                    {new Date(h.run_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={
                        h.ok ? "text-[hsl(var(--primary))]" : "text-amber-500"
                      }
                    >
                      {h.ok ? "ok" : (h.error || "fail")}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{h.items_count}</td>
                  <td className="py-2 pr-3">{h.triggered_by}</td>
                  <td className="py-2 pr-3 font-mono text-[10px]">
                    {(h.slugs || []).join(", ")}
                  </td>
                  <td className="py-2 pr-3">{h.status_code || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
